'use client'

import initializePatientNavigatorCallRoomAction from '@app/p/apply-success/[token]/[step]/initializePatientNavigatorCallRoomAction'
import usePostTrialApplyStepViewedEvent from '@components/hooks/usePostTrialApplyStepViewedEvent'
import { trackEvent } from '@lib/tracking'
import { isInAppBrowser } from '@lib/tracking/isInAppBrowser'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import type { ApplySuccessPageProps } from 'app/p/apply-success/[token]/[step]/page'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'
import { getApplySuccessRouteToRedirectTo } from './getApplySuccessRouteToRedirectTo'
import { VerificationAgentCall } from './PatientNavigator/VerificationAgentCall.client'
import { VerificationAgentIntro } from './PatientNavigator/VerificationAgentIntro'

export const consoleLogger = console

export function PatientNavigatorPhoneCallStep({
  allowPatientCallToSite,
  canPatientContactSite,
  consentForEmr,
  consentToShareYourStory,
  hasPatientNavigatorAgentCallCompleted,
  idToken,
  isFullyQualified,
  isQualifiedApply,
  mostQualifiedTrialApplicationIdToken,
  nctId,
  patientIdToken,
  patientName,
  patientUserAgent,
  showScreeningVisitQualificationStep,
  urlToExitInAppBrowser,
  whyAreYouApplying,
}: Pick<
  ApplySuccessPageProps,
  | 'allowPatientCallToSite'
  | 'canPatientContactSite'
  | 'consentForEmr'
  | 'consentToShareYourStory'
  | 'hasPatientNavigatorAgentCallCompleted'
  | 'idToken'
  | 'isFullyQualified'
  | 'isQualifiedApply'
  | 'mostQualifiedTrialApplicationIdToken'
  | 'nctId'
  | 'patientIdToken'
  | 'patientName'
  | 'patientUserAgent'
  | 'showScreeningVisitQualificationStep'
  | 'urlToExitInAppBrowser'
  | 'whyAreYouApplying'
>) {
  const router = useRouter()
  const [callState, setCallState] = useState<'intro' | 'active' | 'done'>(
    'intro',
  )
  const [microphoneAccessGranted, setMicrophoneAccessGranted] = useState(false)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [isGeneratingToken, setIsGeneratingToken] = useState(false)

  useEffect(() => {
    if (isInAppBrowser()) {
      trackEvent(
        TrackingEvents.PATIENT_NAVIGATOR_AGENT_IN_APP_BROWSER_ENTERED,
        {
          trialApplicationIdToken: idToken,
        },
      )
    }

    const fetchMicrophonePermission = async () => {
      // Some browsers don't support the Permissions API, so automatically skip accepting microphone access
      if (!('permissions' in navigator)) {
        consoleLogger.warn('No Permissions API, so disabling microphone access')
        setMicrophoneAccessGranted(false)
        return
      }

      try {
        const result = await navigator.permissions.query({
          name: 'microphone' as PermissionName,
        })
        setMicrophoneAccessGranted(result.state === 'granted')
      } catch (err) {
        consoleLogger.error('Error checking microphone permission:', err)
        setMicrophoneAccessGranted(false)
      }
    }
    fetchMicrophonePermission()
  }, [])

  usePostTrialApplyStepViewedEvent({
    mostQualifiedTrialApplicationIdToken,
    trialApplicationIdToken: idToken,
    type: 'patient-navigator-agent',
  })

  const goToNextStep = useCallback(() => {
    router.push(
      getApplySuccessRouteToRedirectTo('patient-navigator-agent', {
        allowPatientCallToSite,
        canPatientContactSite,
        consentForEmr,
        consentToShareYourStory,
        hasPatientNavigatorAgentCallCompleted,
        idToken,
        isFullyQualified,
        isQualifiedApply,
        showScreeningVisitQualificationStep,
        whyAreYouApplying,
      }),
    )
  }, [idToken, router])

  const handleStartCall = async () => {
    setIsGeneratingToken(true)

    try {
      const result = await initializePatientNavigatorCallRoomAction({
        idToken,
        nctId,
        patientIdToken,
      })

      if (result.success) {
        trackEvent(
          TrackingEvents.PATIENT_NAVIGATOR_AGENT_START_BUTTON_CLICKED,
          {
            trialApplicationIdToken: idToken,
            val: result.value,
          },
        )
        setAccessToken(result.value)
        setCallState('active')
      } else {
        trackEvent(TrackingEvents.PATIENT_NAVIGATOR_AGENT_CALL_ERRORED, {
          mostQualifiedTrialApplicationIdToken,
          trialApplicationIdToken: idToken,
          type: 'access_token_generation_failed',
          val: result.error,
        })
        goToNextStep()
      }
    } catch (error) {
      trackEvent(TrackingEvents.PATIENT_NAVIGATOR_AGENT_CALL_ERRORED, {
        mostQualifiedTrialApplicationIdToken,
        trialApplicationIdToken: idToken,
        type: 'access_token_generation_error',
        val: String(error),
      })
      goToNextStep()
    } finally {
      setIsGeneratingToken(false)
    }
  }

  if (callState === 'intro') {
    return (
      <VerificationAgentIntro
        allowPatientCallToSite={allowPatientCallToSite}
        canPatientContactSite={canPatientContactSite}
        consentForEmr={consentForEmr}
        consentToShareYourStory={consentToShareYourStory}
        hasPatientNavigatorAgentCallCompleted={
          hasPatientNavigatorAgentCallCompleted
        }
        idToken={idToken}
        isFullyQualified={isFullyQualified}
        isGeneratingToken={isGeneratingToken}
        isQualifiedApply={isQualifiedApply}
        microphoneAccessGranted={microphoneAccessGranted}
        onStartCall={handleStartCall}
        setMicrophoneAccessGranted={setMicrophoneAccessGranted}
        showScreeningVisitQualificationStep={
          showScreeningVisitQualificationStep
        }
        urlToExitInAppBrowser={urlToExitInAppBrowser}
        userAgent={patientUserAgent}
        whyAreYouApplying={whyAreYouApplying}
      />
    )
  }

  return (
    <VerificationAgentCall
      accessToken={accessToken}
      allowPatientCallToSite={allowPatientCallToSite}
      canPatientContactSite={canPatientContactSite}
      consentForEmr={consentForEmr}
      consentToShareYourStory={consentToShareYourStory}
      hasPatientNavigatorAgentCallCompleted={
        hasPatientNavigatorAgentCallCompleted
      }
      idToken={idToken}
      isFullyQualified={isFullyQualified}
      isQualifiedApply={isQualifiedApply}
      mostQualifiedTrialApplicationIdToken={
        mostQualifiedTrialApplicationIdToken
      }
      patientName={patientName ?? ''}
      patientUserAgent={patientUserAgent}
      showScreeningVisitQualificationStep={showScreeningVisitQualificationStep}
      whyAreYouApplying={whyAreYouApplying}
    />
  )
}
